import { useMemo, useRef, useState, type ReactNode } from 'react';
import { highlight } from '@/components/highlight';
import { SearchSelect, type PickSource, type SearchOption } from '@/components/SearchSelect';
import { compare, matchCode, sameCode } from '@/components/codeMatch';
import { matchFarmName, sameFarmName } from '@/components/farmMatch';
import { nextFocusable } from '@/components/focus';
import type { FieldSpan } from '@/components/ui';

export interface PickableFarm {
  id: string;
  code: string;
  nameEn: string;
  nameUr?: string | null;
  district?: string | null;
  /** Deleted farms are passed too, only to explain why one cannot be chosen. */
  isActive: boolean;
  storeCount?: number;
}

/** Stands in for the id of a farm typed in as new, until saving creates it. */
export const NEW_FARM = 'new';

type Box = 'code' | 'name';

/**
 * Picks a farm with two linked boxes, the way `StorePicker` picks a store:
 * its code and its name.
 *
 * The boxes always show one farm. The other box changes the moment the text
 * typed names a farm — "8", "frm8" or "FRM-08" in the code box, the full
 * name in the name box — with no Tab or click needed. Erasing either box
 * clears both; leaving one with text that names no farm clears both too, and
 * says why, so the pair never shows two different farms.
 *
 * Given `onCreate`, a name that matches no farm can be added as a new one.
 * Its code is the server's to give, so the code box shows the one it is
 * about to get (`newFarm.code`), never a box to type one into.
 */
export function FarmPicker({
  value,
  onChange,
  farms,
  newFarm,
  onCreate,
  fallback,
  detail,
  required,
  disabled,
  hint,
  span,
}: {
  /** A farm id, `NEW_FARM`, or '' for none. */
  value: string;
  onChange: (farmId: string) => void;
  /** Active and deleted farms; undefined while they are still loading. */
  farms: PickableFarm[] | undefined;
  /** The farm being added, while `value` is `NEW_FARM`. */
  newFarm?: { name: string; code?: string | undefined } | undefined;
  onCreate?: ((name: string) => void) | undefined;
  /** Shown for a chosen farm that is not in `farms` — the deleted farm of a
   *  store being edited. */
  fallback?: { code: string; name: string } | undefined;
  /** What a dropdown row says about each farm in place of its store count —
   *  e.g. its empty sheds, on a form that fills them. */
  detail?: ((farm: PickableFarm) => string) | undefined;
  required?: boolean | undefined;
  disabled?: boolean | undefined;
  hint?: ReactNode;
  span?: FieldSpan | undefined;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  /** Text a box was left holding that names no farm, until the next change. */
  const [miss, setMiss] = useState<{ by: Box; text: string } | null>(null);
  const loading = farms === undefined;

  const { byId, deleted, codeOptions, nameOptions } = useMemo(() => {
    const all = farms ?? [];
    const live = all.filter((f) => f.isActive);
    return {
      byId: new Map(live.map((f) => [f.id, f])),
      deleted: all.filter((f) => !f.isActive),
      // Each box searches what it is labelled with: the code box codes only,
      // the name box names (English or Urdu), codes and districts.
      codeOptions: [...live]
        .sort((a, b) => compare(a.code, b.code))
        .map((f): SearchOption => ({ value: f.id, label: f.code })),
      nameOptions: [...live]
        .sort((a, b) => compare(a.nameEn, b.nameEn))
        .map((f): SearchOption => ({
          value: f.id,
          label: f.nameEn,
          keywords: [f.nameUr, f.code, f.district].filter(Boolean).join(' '),
        })),
    };
  }, [farms]);
  const total = codeOptions.length;

  const isNew = value === NEW_FARM;
  const chosen = byId.get(value);
  // A farm not among those loaded shows as `fallback`, or as empty rather
  // than as a raw id.
  const shownCode = isNew ? (newFarm?.code ?? '') : chosen ? undefined : (fallback?.code ?? '');
  const shownName = isNew ? (newFarm?.name ?? '') : chosen ? undefined : (fallback?.name ?? '');

  function pick(option: SearchOption | null) {
    setMiss(null);
    onChange(option?.value ?? '');
  }

  // A code chosen from the keyboard has already filled the name, so move on
  // past it to whatever follows.
  function afterCodePick(_option: SearchOption, source: PickSource) {
    if (source !== 'enter' && source !== 'tab') return;
    setTimeout(() => {
      const nameInput = wrapRef.current?.querySelectorAll<HTMLInputElement>('input')[1];
      if (nameInput) nextFocusable(nameInput)?.focus();
    }, 0);
  }

  function row(option: SearchOption, query: string, by: Box) {
    const farm = byId.get(option.value);
    if (!farm) return option.label;
    const extra = detail
      ? detail(farm)
      : farm.storeCount === undefined
        ? null
        : `${farm.storeCount} ${farm.storeCount === 1 ? 'store' : 'stores'}`;
    const main = by === 'code' ? farm.code : farm.nameEn;
    const rest = [by === 'code' ? farm.nameEn : farm.code, farm.district, extra].filter(Boolean).join(' · ');
    return (
      <span className="pickopt">
        <span className={`pickopt__main${by === 'code' ? ' pickopt__main--code' : ''}`} title={main}>
          {highlight(main, query)}
        </span>
        <span className="pickopt__sub" title={rest}>
          {rest}
        </span>
      </span>
    );
  }

  /** Why `typed` names no farm in box `by`: a deleted farm holds it, there
   *  are no farms at all, or it is simply not there — and what to do. */
  function noFarm(by: Box, typed: string): string {
    const gone = typed
      ? deleted.find((f) => (by === 'code' ? sameCode({ value: f.id, label: f.code }, typed) : sameFarmName(f.nameEn, typed)))
      : undefined;
    if (gone) return `${gone.code} “${gone.nameEn}” is deleted. Reactivate it in Farm Manager.`;
    if (total === 0) return onCreate ? 'No farms yet. Type a name in Farm name to add one.' : 'No farms to choose from';
    if (!typed) return by === 'code' ? 'No farm with that code' : 'No farm with that name';
    if (by === 'code') {
      return `No farm with code “${typed}”.${onCreate ? ' To add a new farm, type its name in Farm name.' : ''}`;
    }
    return `No farm named “${typed}”.${onCreate ? ' Type it again and choose “+ Add” to add it as a new farm.' : ''}`;
  }

  const header = (shown: number) =>
    shown === total ? `${total} ${total === 1 ? 'farm' : 'farms'}` : `${shown} of ${total} farms`;
  const off = disabled || loading;
  const spanCls = span === 2 ? ' formgrid__span-2' : span === 'full' ? ' formgrid__full' : '';

  return (
    <div ref={wrapRef} className={`farmpicker${spanCls}`} role="group" aria-label="Farm">
      <div className="farmpicker__fields">
        <SearchSelect
          label="Farm code"
          value={value}
          selectedLabel={shownCode}
          onChange={pick}
          options={codeOptions}
          required={required}
          disabled={off}
          placeholder={loading ? 'Loading…' : isNew ? 'On save' : 'Code'}
          emptyText={(typed) => noFarm('code', typed)}
          commitExact
          commitWhileTyping
          clearable
          onUnmatched={(text) => setMiss({ by: 'code', text })}
          match={matchCode}
          isExact={sameCode}
          onPick={afterCodePick}
          renderOption={(o, q) => row(o, q, 'code')}
          listHeader={header}
        />
        <SearchSelect
          label="Farm name"
          value={value}
          selectedLabel={shownName}
          onChange={pick}
          options={nameOptions}
          required={required}
          disabled={off}
          placeholder={loading ? 'Loading farms…' : 'Search by farm name…'}
          emptyText={(typed) => noFarm('name', typed)}
          commitExact
          commitWhileTyping
          clearable
          onUnmatched={(text) => setMiss({ by: 'name', text })}
          match={matchFarmName}
          isExact={(o, q) => sameFarmName(o.label, q)}
          {...(onCreate
            ? {
                onCreate: (name: string) => {
                  setMiss(null);
                  onCreate(name);
                },
                createLabel: (name: string) => `+ Add “${name}” as a new farm`,
              }
            : {})}
          renderOption={(o, q) => row(o, q, 'name')}
          listHeader={header}
        />
      </div>
      {miss && (
        <span className="settings__hint farmpicker__miss" role="alert">
          {noFarm(miss.by, miss.text)}
        </span>
      )}
      {isNew && (
        <span className="settings__hint farmpicker__hint" role="status">
          <span className="pill">New</span>{' '}
          {newFarm?.code ? `Gets code ${newFarm.code} when you save` : 'Gets its code when you save'}, and is added
          with this store. Farm Manager then lists it for review, to fill in its details.
        </span>
      )}
      {hint && <span className="settings__hint farmpicker__hint">{hint}</span>}
    </div>
  );
}
